/**
 * @typedef {import('../types/support').JiraAdfNode} JiraAdfNode
 *
 * @typedef {{
 *   type: string;
 *   content?: TiptapJSON[];
 *   text?: string;
 *   marks?: { type: string; attrs?: Record<string, unknown> }[];
 *   attrs?: Record<string, unknown>;
 * }} TiptapJSON
 */

const FALLBACK_DOC = {
  type: 'doc',
  version: 1,
  content: [{ type: 'paragraph', content: [{ type: 'text', text: ' ' }] }],
};

/**
 * Converte marks do TipTap para ADF.
 * @param {{ type: string; attrs?: Record<string, unknown> }[] | undefined} marks
 * @returns {JiraAdfNode['marks']}
 */
function convertMarks(marks) {
  if (!marks) return undefined;
  const result = [];
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        result.push({ type: 'strong' });
        break;
      case 'italic':
        result.push({ type: 'em' });
        break;
      case 'underline':
        result.push({ type: 'underline' });
        break;
      case 'code':
        result.push({ type: 'code' });
        break;
      case 'link':
        if (mark.attrs?.href) {
          result.push({ type: 'link', attrs: { href: mark.attrs.href } });
        }
        break;
      case 'textStyle':
        if (mark.attrs?.color) {
          result.push({ type: 'textColor', attrs: { color: mark.attrs.color } });
        }
        break;
      default:
        break;
    }
  }
  return result.length ? result : undefined;
}

/**
 * Converte um nó TipTap para nó ADF.
 * @param {TiptapJSON} node
 * @returns {JiraAdfNode | null}
 */
function convertNode(node) {
  if (!node) return null;

  switch (node.type) {
    case 'text': {
      /** @type {JiraAdfNode} */
      const textNode = { type: 'text', text: node.text || '' };
      const marks = convertMarks(node.marks);
      if (marks) textNode.marks = marks;
      return textNode;
    }

    case 'paragraph': {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      if (!children.length) return null;
      return { type: 'paragraph', content: children };
    }

    case 'heading': {
      const level = typeof node.attrs?.level === 'number'
        ? Math.min(6, Math.max(1, node.attrs.level))
        : 1;
      const children = (node.content || []).map(convertNode).filter(Boolean);
      if (!children.length) return null;
      return { type: 'heading', attrs: { level }, content: children };
    }

    case 'bulletList': {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      return { type: 'bulletList', content: children };
    }

    case 'orderedList': {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      return { type: 'orderedList', content: children };
    }

    case 'listItem': {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      return { type: 'listItem', content: children };
    }

    case 'blockquote': {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      return { type: 'blockquote', content: children };
    }

    case 'codeBlock': {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      return { type: 'codeBlock', content: children.length ? children : [{ type: 'text', text: '' }] };
    }

    case 'horizontalRule':
      return { type: 'rule' };

    case 'hardBreak':
      return { type: 'hardBreak' };

    default: {
      const children = (node.content || []).map(convertNode).filter(Boolean);
      if (children.length) return { type: node.type, content: children };
      return null;
    }
  }
}

const ADF_BLOCK_TYPES = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
  'rule',
]);

/**
 * Jira exige que o content do doc contenha apenas nós de bloco. Caso algum
 * nó inline (texto, hardBreak) apareça solto no nível raiz — por exemplo
 * quando o editor não envolveu o texto digitado em um parágrafo — esta
 * função os agrupa em um parágrafo, evitando um payload ADF inválido.
 * @param {JiraAdfNode[]} nodes
 * @returns {JiraAdfNode[]}
 */
function wrapAdfStrayInlineNodes(nodes) {
  const result = [];
  let buffer = [];

  function flushBuffer() {
    if (buffer.length) {
      result.push({ type: 'paragraph', content: buffer });
      buffer = [];
    }
  }

  for (const node of nodes) {
    if (!node) continue;
    if (ADF_BLOCK_TYPES.has(node.type)) {
      flushBuffer();
      result.push(node);
    } else {
      buffer.push(node);
    }
  }
  flushBuffer();

  return result;
}

/**
 * Converte JSON do editor para um documento ADF Jira.
 * @param {TiptapJSON | null | undefined} json
 * @returns {JiraAdfNode}
 */
export function tiptapJsonToAdf(json) {
  if (!json || !json.content || !Array.isArray(json.content)) {
    return FALLBACK_DOC;
  }

  const converted = json.content.map(convertNode).filter(Boolean);
  const content = wrapAdfStrayInlineNodes(converted);

  if (!content.length) return FALLBACK_DOC;

  return {
    type: 'doc',
    version: 1,
    content,
  };
}

/**
 * Verifica se um documento ADF tem texto real.
 * @param {TiptapJSON | null | undefined} json
 * @returns {boolean}
 */
export function hasRealContent(json) {
  if (!json || !json.content) return false;
  const text = extractTiptapText(json);
  return text.trim().length > 0;
}

/**
 * @param {TiptapJSON | undefined | null} node
 * @returns {string}
 */
function extractTiptapText(node) {
  if (!node) return '';
  if (typeof node.text === 'string') return node.text;
  if (Array.isArray(node.content)) {
    return node.content.map(extractTiptapText).join(' ');
  }
  return '';
}
